import React, { useState, useEffect, useMemo } from "react";
import { displayDate } from "../../utils/dateUtils";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Tooltip,
  Tab,
  Tabs,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  LinearProgress,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ListSubheader,
} from "@mui/material";
import {
  Add,
  Edit,
  Close,
  Visibility,
  Policy,
  Security,
  Gavel,
  Send,
  CheckCircle,
  People,
  ContentCopy,
  Download,
  Publish,
  Archive,
  Delete,
  WarningAmber,
} from "@mui/icons-material";
import api from "../../services/api";
import Toast from "../../components/common/Toast";
import FormDatePicker from "../../components/forms/FormDatePicker";
import { useRoles } from "../../hooks/queries/useRBAC";

/** Preset audiences (legacy + profile-based); values are stored as-is on consent_forms.targetAudience */
const PRESET_TARGET_AUDIENCE = [
  { value: "all", label: "All users" },
  { value: "students", label: "Students only (all student profiles)" },
  { value: "teachers", label: "Teachers only (all teacher profiles)" },
  { value: "staff", label: "Staff (teacher, HOD, admin, institute)" },
  { value: "parents", label: "Parents / guardians" },
];

const PRESET_VALUE_SET = new Set(PRESET_TARGET_AUDIENCE.map((p) => p.value));

function labelForTargetAudience(targetAudience, roleLabelBySlug) {
  const raw = String(targetAudience ?? "all").trim() || "all";
  const key = raw.toLowerCase();
  const preset = PRESET_TARGET_AUDIENCE.find((p) => p.value === key);
  if (preset) return preset.label;
  return roleLabelBySlug.get(key) || raw;
}

const ConsentFormManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    type: "privacy_policy",
    version: "1.0",
    content: "",
    summary: "",
    isRequired: true,
    targetAudience: "all",
    effectiveDate: "",
    expiryDate: "",
  });

  const formTypes = [
    {
      value: "privacy_policy",
      label: "Privacy Policy",
      icon: <Security />,
      color: "#1976d2",
    },
    {
      value: "terms_of_service",
      label: "Terms of Service",
      icon: <Gavel />,
      color: "#7b1fa2",
    },
    {
      value: "data_collection",
      label: "Data Collection Consent",
      icon: <Policy />,
      color: "#00897b",
    },
    {
      value: "photo_consent",
      label: "Photo/Media Consent",
      icon: <Policy />,
      color: "#f57c00",
    },
    {
      value: "communication",
      label: "Communication Consent",
      icon: <Send />,
      color: "#5c6bc0",
    },
    {
      value: "custom",
      label: "Custom Consent",
      icon: <Policy />,
      color: "#607d8b",
    },
  ];

  const [consentForms, setConsentForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastSeverity, setToastSeverity] = useState("success");
  const [exporting, setExporting] = useState(false);
  const [savingForm, setSavingForm] = useState(false);
  const [deletingForm, setDeletingForm] = useState(false);
  const [duplicatingFormId, setDuplicatingFormId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const { data: rolesPayload, isLoading: rolesLoading } = useRoles();

  const roleLabelBySlug = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(rolesPayload?.data)
      ? rolesPayload.data
      : Array.isArray(rolesPayload)
      ? rolesPayload
      : [];
    for (const r of rows) {
      if (r?.isActive === false) continue;
      const slug = r?.slug || r?.role;
      if (!slug) continue;
      map.set(String(slug).toLowerCase(), r.displayName || r.name || String(slug));
    }
    return map;
  }, [rolesPayload]);

  const audienceRolesForSelect = useMemo(() => {
    const rows = Array.isArray(rolesPayload?.data)
      ? rolesPayload.data
      : Array.isArray(rolesPayload)
      ? rolesPayload
      : [];
    const seen = new Set(PRESET_VALUE_SET);
    const out = [];
    for (const r of rows) {
      if (r?.isActive === false) continue;
      const slug = r?.slug || r?.role;
      if (!slug) continue;
      const k = String(slug).toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ value: slug, label: r.displayName || r.name || String(slug) });
    }
    out.sort((a, b) => a.label.localeCompare(b.label));
    return out;
  }, [rolesPayload]);

  const orphanAudienceOption = useMemo(() => {
    const v = formData.targetAudience;
    if (v == null || v === "") return null;
    const key = String(v).toLowerCase();
    if (PRESET_VALUE_SET.has(key)) return null;
    const inRoles = audienceRolesForSelect.some(
      (r) => String(r.value).toLowerCase() === key
    );
    if (inRoles) return null;
    return { value: v, label: `${v} (custom audience)` };
  }, [formData.targetAudience, audienceRolesForSelect]);

  useEffect(() => {
    fetchConsentForms();
  }, []);

  const fetchConsentForms = async () => {
    setLoadingForms(true);
    try {
      const res = await api.get("/super/admin/consent-forms");
      const payload = res.data;
      const data = Array.isArray(payload)
        ? payload
        : (payload && Array.isArray(payload.data))
        ? payload.data
        : [];
      setConsentForms(data);
    } catch (err) {
      console.error("Failed to load consent forms", err);
    } finally {
      setLoadingForms(false);
    }
  };

  const handleExportResponses = async (form) => {
    try {
      if (!form?.id) return;
      setExporting(true);
      const res = await api.get(`/super/admin/consent-forms/${form.id}/export-responses`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `consent_form_${form.id}_responses.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setToastSeverity("success");
      setToastMessage("Export started");
    } catch (e) {
      setToastSeverity("error");
      setToastMessage(e?.response?.data?.message || e?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const getFormTypeInfo = (type) => {
    return formTypes.find((ft) => ft.value === type) || formTypes[5];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "draft":
        return "warning";
      case "archived":
        return "default";
      case "expired":
        return "error";
      default:
        return "default";
    }
  };
  const normStatus = (s) => String(s || "").toLowerCase();

  const computePercentage = (count, total) => {
    const t = Number(total) || 0;
    if (t <= 0) return 0;
    const c = Number(count) || 0;
    return Math.round((c / t) * 100);
  };

  const validateForm = () => {
    const err = {};
    if (!(formData.title || "").trim()) err.title = "Form title is required";
    if (!(formData.type || "").trim()) err.type = "Form type is required";
    if (!(formData.version || "").trim()) err.version = "Version is required";
    if (!(formData.summary || "").trim()) err.summary = "Summary is required";
    if (!(formData.content || "").trim()) err.content = "Full content is required";
    if (!(formData.effectiveDate || "").trim()) err.effectiveDate = "Effective date is required";
    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleCreateNew = () => {
    setFormData({
      title: "",
      type: "privacy_policy",
      version: "1.0",
      content: "",
      summary: "",
      isRequired: true,
      targetAudience: "all",
      effectiveDate: "",
      expiryDate: "",
    });
    setFormErrors({});
    setSelectedForm(null);
    setCreateDialogOpen(true);
  };

  const handleEditForm = (form) => {
    setFormData({
      title: form.title,
      type: form.type,
      version: form.version,
      content: form.content,
      summary: form.summary,
      isRequired: form.isRequired,
      targetAudience: form.targetAudience,
      effectiveDate: form.effectiveDate,
      expiryDate: form.expiryDate || "",
    });
    setFormErrors({});
    setSelectedForm(form);
    setCreateDialogOpen(true);
  };

  const handleViewForm = (form) => {
    setSelectedForm(form);
    setViewDialogOpen(true);
  };

  const handlePublishForm = (form) => {
    setSelectedForm(form);
    setPublishDialogOpen(true);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveDraft = async () => {
    try {
      setSavingForm(true);
      if (selectedForm && selectedForm.id) {
        await api.put(`/super/admin/consent-forms/${selectedForm.id}`, {
          ...formData,
          status: "draft",
        });
        setToastMessage("Consent form updated successfully");
        setToastSeverity("success");
      } else {
        await api.post(`/super/admin/consent-forms`, {
          ...formData,
          status: "draft",
        });
        setToastMessage("Consent form created successfully");
        setToastSeverity("success");
      }
      setCreateDialogOpen(false);
      setFormErrors({});
      fetchConsentForms();
    } catch (err) {
      console.error(err);
      setToastMessage("Failed to save draft");
      setToastSeverity("error");
    } finally {
      setSavingForm(false);
    }
  };

  const handleCreateOrUpdate = async () => {
    if (!validateForm()) return;
    await handleSaveDraft();
  };

  const handleConfirmPublish = async () => {
      if (!selectedForm?.id) return;
    try {
      await api.post(`/super/admin/consent-forms/${selectedForm.id}/publish`);
      setPublishDialogOpen(false);
      fetchConsentForms();
    } catch (err) {
      console.error(err);
      setToastMessage("Failed to publish form");
      setToastSeverity("error");
    }
  };

  const handleArchiveForm = async (form) => {
    if (!form?.id) return;
    try {
      await api.post(`/super/admin/consent-forms/${form.id}/archive`);
      setToastMessage("Consent form archived successfully");
      setToastSeverity("success");
      fetchConsentForms();
    } catch (err) {
      console.error(err);
      setToastMessage("Failed to archive form");
      setToastSeverity("error");
    }
  };

  const handleDuplicateForm = async (form) => {
    if (!form?.id) return;
    try {
      setDuplicatingFormId(form.id);
      await api.post(`/super/admin/consent-forms/${form.id}/duplicate`);
      setToastMessage("Form duplicated");
      setToastSeverity("success");
      fetchConsentForms();
      // don't open modal on duplicate; simply refresh list and notify
    } catch (err) {
      console.error(err);
      setToastMessage("Failed to duplicate form");
      setToastSeverity("error");
    } finally {
      setDuplicatingFormId(null);
    }
  };

  const handleDeleteForm = async (form) => {
    try {
      if (!form?.id) return;
      setDeletingForm(true);
      await api.delete(`/super/admin/consent-forms/${form.id}`);
      setToastMessage("Consent form deleted successfully");
      setToastSeverity("success");
      setDeleteDialogOpen(false);
      setFormToDelete(null);
      fetchConsentForms();
    } catch (err) {
      console.error(err);
      setToastMessage("Failed to delete form");
      setToastSeverity("error");
    } finally {
      setDeletingForm(false);
    }
  };

  const filteredForms = consentForms.filter((form) => {
    const st = normStatus(form.status);
    if (activeTab === 0) return true;
    if (activeTab === 1) return st === "active";
    if (activeTab === 2) return st === "draft";
    if (activeTab === 3) return st === "archived";
    return true;
  });

  const stats = {
    total: consentForms.length,
    active: consentForms.filter((f) => normStatus(f.status) === "active").length,
    drafts: consentForms.filter((f) => normStatus(f.status) === "draft").length,
    pendingResponses: consentForms
      .filter((f) => normStatus(f.status) === "active")
      .reduce((sum, f) => sum + f.pendingCount, 0),
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Consent Form Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage privacy policies, terms of service, and consent
            forms
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateNew}
        >
          Create New Form
        </Button>
      </Box>

      {/* Alert for pending consents */}
      {stats.pendingResponses > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningAmber />}>
          <Typography variant="body2">
            <strong>{stats.pendingResponses} users</strong> have not yet
            responded to active consent forms. They will be prompted to accept
            upon their next login.
          </Typography>
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <Policy sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Forms
              </Typography>
            </CardContent>
          </Card>

            {/* Toast Notification */}
            <Toast
              open={!!toastMessage}
              message={toastMessage}
              severity={toastSeverity}
              onClose={() => setToastMessage(null)}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
            />
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <CheckCircle
                sx={{ fontSize: 40, color: "success.main", mb: 1 }}
              />
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {stats.active}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <Edit sx={{ fontSize: 40, color: "warning.main", mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {stats.drafts}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Drafts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <People sx={{ fontSize: 40, color: "error.main", mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="error.main">
                {stats.pendingResponses}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Responses
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{ px: 2 }}
          >
            <Tab label="All Forms" />
            <Tab label="Active" />
            <Tab label="Drafts" />
            <Tab label="Archived" />
          </Tabs>
        </Box>

        {/* Forms List */}
        <Box sx={{ p: 2 }}>
          {loadingForms && (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading consent forms...
              </Typography>
            </Box>
          )}
          {!loadingForms && filteredForms.length === 0 && (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No consent forms found.
              </Typography>
            </Box>
          )}
          <Grid container spacing={2}>
            {filteredForms.map((form) => {
              const typeInfo = getFormTypeInfo(form.type);
              const acceptanceRate =
                form.totalUsers > 0
                  ? Math.round((form.acceptedCount / form.totalUsers) * 100)
                  : 0;

              return (
                <Grid item xs={12} md={6} key={form.id}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Avatar sx={{ bgcolor: typeInfo.color }}>
                            {typeInfo.icon}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {form.title}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {typeInfo.label} • v{form.version}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Chip
                            label={form.status}
                            size="small"
                            color={getStatusColor(form.status)}
                            sx={{ textTransform: "capitalize" }}
                          />
                          {form.isRequired && (
                            <Chip
                              label="Required"
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {form.summary}
                      </Typography>

                      {normStatus(form.status) === "active" && (
                        <Box sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 0.5,
                            }}
                          >
                            <Typography variant="caption">
                              Acceptance Rate
                            </Typography>
                            <Typography variant="caption" fontWeight="bold">
                              {acceptanceRate}% ({form.acceptedCount}/
                              {form.totalUsers})
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={acceptanceRate}
                            sx={{ height: 8, borderRadius: 4 }}
                            color={
                              acceptanceRate >= 90
                                ? "success"
                                : acceptanceRate >= 70
                                ? "warning"
                                : "error"
                            }
                          />
                          {form.pendingCount > 0 && (
                            <Typography variant="caption" color="warning.main">
                              {form.pendingCount} pending responses
                            </Typography>
                          )}
                        </Box>
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Created:{" "}
                          {displayDate(form.createdAt)}
                        </Typography>
                        <Box>
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={() => handleViewForm(form)}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {normStatus(form.status) === "draft" && (
                            <>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditForm(form)}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Publish">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handlePublishForm(form)}
                                >
                                  <Publish fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {normStatus(form.status) === "active" && (
                            <Tooltip title="Archive">
                              <IconButton size="small" color="default" onClick={() => handleArchiveForm(form)}>
                                <Archive fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {normStatus(form.status) !== "active" && (
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => { setFormToDelete(form); setDeleteDialogOpen(true); }}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Duplicate">
                            <IconButton size="small" onClick={() => handleDuplicateForm(form)} disabled={duplicatingFormId === form.id}>
                              {duplicatingFormId === form.id ? <CircularProgress size={16} /> : <ContentCopy fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          if (savingForm) return;
          setCreateDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">
              {selectedForm ? "Edit Consent Form" : "Create New Consent Form"}
            </Typography>
            <IconButton onClick={() => setCreateDialogOpen(false)} disabled={savingForm}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Form Title"
                fullWidth
                required
                disabled={savingForm}
                value={formData.title}
                onChange={(e) => {
                  handleFormChange("title", e.target.value);
                  if (formErrors.title) setFormErrors((prev) => ({ ...prev, title: "" }));
                }}
                placeholder="e.g., Data Privacy Policy v2.0"
                error={!!formErrors.title}
                helperText={formErrors.title}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!formErrors.type} disabled={savingForm}>
                <InputLabel>Form Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Form Type"
                  onChange={(e) => {
                    handleFormChange("type", e.target.value);
                    if (formErrors.type) setFormErrors((prev) => ({ ...prev, type: "" }));
                  }}
                >
                  {formTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Avatar
                          sx={{ bgcolor: type.color, width: 24, height: 24 }}
                        >
                          {React.cloneElement(type.icon, {
                            sx: { fontSize: 14 },
                          })}
                        </Avatar>
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.type && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {formErrors.type}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Version"
                fullWidth
                required
                disabled={savingForm}
                value={formData.version}
                onChange={(e) => {
                  handleFormChange("version", e.target.value);
                  if (formErrors.version) setFormErrors((prev) => ({ ...prev, version: "" }));
                }}
                placeholder="e.g., 1.0, 2.1"
                error={!!formErrors.version}
                helperText={formErrors.version}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Summary (shown to users)"
                fullWidth
                required
                multiline
                rows={2}
                disabled={savingForm}
                value={formData.summary}
                onChange={(e) => {
                  handleFormChange("summary", e.target.value);
                  if (formErrors.summary) setFormErrors((prev) => ({ ...prev, summary: "" }));
                }}
                placeholder="Brief description of what this consent form covers..."
                error={!!formErrors.summary}
                helperText={formErrors.summary}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Full Content"
                fullWidth
                required
                multiline
                rows={8}
                disabled={savingForm}
                value={formData.content}
                onChange={(e) => {
                  handleFormChange("content", e.target.value);
                  if (formErrors.content) setFormErrors((prev) => ({ ...prev, content: "" }));
                }}
                placeholder="Enter the complete policy/terms text here..."
                error={!!formErrors.content}
                helperText={formErrors.content || "This is the full document that users will see when they click 'Read More'"}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>
                Settings
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isRequired}
                    onChange={(e) =>
                      handleFormChange("isRequired", e.target.checked)
                    }
                    disabled={savingForm}
                  />
                }
                label="Required (users must accept to continue)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={savingForm}>
                <InputLabel>Target Audience</InputLabel>
                <Select
                  value={formData.targetAudience}
                  label="Target Audience"
                  onChange={(e) =>
                    handleFormChange("targetAudience", e.target.value)
                  }
                >
                  <ListSubheader>Presets</ListSubheader>
                  {PRESET_TARGET_AUDIENCE.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                  <ListSubheader>Roles (from directory)</ListSubheader>
                  {rolesLoading ? (
                    <MenuItem disabled>Loading roles…</MenuItem>
                  ) : (
                    audienceRolesForSelect.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))
                  )}
                  {orphanAudienceOption ? (
                    <MenuItem value={orphanAudienceOption.value}>
                      {orphanAudienceOption.label}
                    </MenuItem>
                  ) : null}
                </Select>
                {rolesLoading ? (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    Role list is loading; presets are available now.
                  </Typography>
                ) : null}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormDatePicker
                label="Effective Date"
                required
                disabled={savingForm}
                value={formData.effectiveDate || null}
                onChange={(d) => {
                  handleFormChange(
                    "effectiveDate",
                    d ? d.toISOString().split("T")[0] : ""
                  );
                  if (formErrors.effectiveDate) setFormErrors((prev) => ({ ...prev, effectiveDate: "" }));
                }}
                inputFormat="yyyy-MM-dd"
                error={formErrors.effectiveDate}
                touched={!!formErrors.effectiveDate}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormDatePicker
                label="Expiry Date (Optional)"
                disabled={savingForm}
                value={formData.expiryDate || null}
                onChange={(d) =>
                  handleFormChange("expiryDate", d ? d.toISOString().split("T")[0] : "")
                }
                inputFormat="yyyy-MM-dd"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={savingForm}>Cancel</Button>
          <Button variant="outlined" onClick={handleSaveDraft} disabled={savingForm}>
            Save as Draft
          </Button>
          <Button
            variant="contained"
            startIcon={selectedForm ? <Edit /> : <Add />}
            onClick={handleCreateOrUpdate}
            disabled={savingForm}
          >
            {selectedForm ? "Update Form" : "Create Form"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setViewDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        {selectedForm && (
          <>
            <DialogTitle>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar
                    sx={{ bgcolor: getFormTypeInfo(selectedForm.type).color }}
                  >
                    {getFormTypeInfo(selectedForm.type).icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{selectedForm.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Version {selectedForm.version} •{" "}
                      {getFormTypeInfo(selectedForm.type).label}
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={() => setViewDialogOpen(false)}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedForm.status}
                    size="small"
                    color={getStatusColor(selectedForm.status)}
                    sx={{ textTransform: "capitalize", mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Target Audience
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {labelForTargetAudience(selectedForm.targetAudience, roleLabelBySlug)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {displayDate(selectedForm.createdAt)} by{" "}
                    {selectedForm.createdBy}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Effective Date
                  </Typography>
                  <Typography variant="body2">
                    {displayDate(selectedForm.effectiveDate)}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Summary
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedForm.summary}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Full Content
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, maxHeight: 300, overflow: "auto" }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {selectedForm.content}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
              <Button variant="outlined" startIcon={<Download />} onClick={() => handleExportResponses(selectedForm)} disabled={exporting}>
                Export Responses
              </Button>
              {normStatus(selectedForm.status) === "draft" && (
                <Button
                  variant="contained"
                  startIcon={<Publish />}
                  onClick={() => {
                    setViewDialogOpen(false);
                    handlePublishForm(selectedForm);
                  }}
                >
                  Publish
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog
        open={publishDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setPublishDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        {selectedForm && (
          <>
            <DialogTitle>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Publish color="primary" />
                <Typography variant="h6">Publish Consent Form</Typography>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Alert severity="info" sx={{ mb: 2 }}>
                Publishing this form will make it visible to all targeted users.
                They will be required to accept it upon their next login.
              </Alert>

              <Typography variant="subtitle2" gutterBottom>
                Form Details:
              </Typography>
              <Box sx={{ bgcolor: "grey.100", p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="body2">
                  <strong>Title:</strong> {selectedForm.title}
                </Typography>
                <Typography variant="body2">
                  <strong>Version:</strong> {selectedForm.version}
                </Typography>
                <Typography variant="body2">
                  <strong>Type:</strong>{" "}
                  {getFormTypeInfo(selectedForm.type).label}
                </Typography>
                <Typography variant="body2">
                  <strong>Target:</strong>{" "}
                  {labelForTargetAudience(selectedForm.targetAudience, roleLabelBySlug)}
                </Typography>
                <Typography variant="body2">
                  <strong>Required:</strong>{" "}
                  {selectedForm.isRequired
                    ? "Yes (must accept)"
                    : "No (optional)"}
                </Typography>
              </Box>

              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>Note:</strong> If this form replaces an existing
                  active form of the same type, the previous version will be
                  automatically archived and all users will need to accept this
                  new version.
                </Typography>
              </Alert>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setPublishDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Publish />}
                onClick={handleConfirmPublish}
              >
                Confirm & Publish
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; if (deletingForm) return; setDeleteDialogOpen(false); setFormToDelete(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Consent Form</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Are you sure you want to delete "{formToDelete?.title || ""}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteDialogOpen(false); setFormToDelete(null); }} disabled={deletingForm}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDeleteForm(formToDelete)} disabled={deletingForm}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsentFormManagement;
